import { Card, Skeleton, notification, theme } from 'antd';
import { CardHeader } from 'components/headings';
import ClientForm from 'components/forms/ClientForm';
import CompliancePanel from 'components/admin/compliance/CompliancePanel';
import RegulatoryAlertsBanner from 'components/admin/compliance/RegulatoryAlertsBanner';
import useTenantFlags from 'hooks/useTenantFlags';
import { useCliente } from 'providers/Clientes/ClienteProvider';
import { useClienteCompliance } from 'providers/Compliance';
import React, { useEffect, useMemo } from 'react';

// Landing de la ficha de cliente (tab `general`). Acá vive:
//  1. El formulario de datos del cliente con su botón Guardar (antes estaba en
//     un Drawer detrás del botón "Editar" del header; ahora editás inline).
//  2. El contenido MINVU: panel de cumplimiento + banner de alertas, debajo.
//
// El formulario y el header comparten el mismo ClienteProvider (montado en los
// HOCs por rol), así que al guardar acá y refrescar con call(id) el header de
// identidad se re-sincroniza solo.
//
// Los widgets MINVU son del módulo de certificación: cuando el tenant lo tiene
// desactivado (minvu_enabled=0) NO se montan ni se dispara su fetch — los
// endpoints /clientes/:id/compliance y /clientes/:id/alerts están gateados por
// requireMinvu y responden 403 MINVU_DISABLED. Por eso pasamos `null` al hook de
// compliance y no renderizamos el banner cuando MINVU está off.
const ClienteInicioPage = ({ id }) => {
  const { token } = theme.useToken();
  const { minvuEnabled, readOnly } = useTenantFlags();
  const { data, loading, call, update } = useCliente();

  // Carga los datos del cliente al montar (y al cambiar de cliente). En el flujo
  // del ejecutivo el fetch lo dispara ClienteHeader, pero el portal admin
  // (AdminClientePage) NO monta ese header — usa su propio <Select> —, así que
  // sin esto `data` nunca se puebla y el form "Datos del cliente" sale vacío.
  // La guarda `data?.id !== id` evita un GET redundante cuando el header ya cargó
  // este mismo cliente y previene re-disparos (no reintroduce el loop del BUG 2).
  useEffect(() => {
    if (id && String(data?.id) !== String(id)) call(id);
  }, [id, data, call]);

  const {
    data: complianceData,
    loading: complianceLoading,
    error: complianceError,
  } = useClienteCompliance(minvuEnabled ? id : null);

  // formProps memoizado por `data`: ClientForm hace resetFields() cuando cambia
  // formProps. Si lo dejáramos inline, cualquier re-render (p.ej. cuando carga
  // el panel MINVU) pisaría lo que el usuario esté tipeando.
  const formProps = useMemo(() => ({ initialValues: data }), [data]);

  return (
    <div className="space-y-4">
      {/* Formulario de datos del cliente (protagonista del tab) */}
      <Card
        bordered
        styles={{ body: { padding: 0 } }}
        style={{ overflow: 'hidden', boxShadow: token.boxShadowTertiary }}
      >
        <CardHeader icon={null} title="Datos del cliente" />
        <div style={{ padding: token.paddingLG }}>
          {loading && !data ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : (
            // En read-only (admin en plan lite / tenant read_only) el form queda
            // deshabilitado — mismo guard que tenía el botón "Editar" del header.
            // El backend igual lo bloquea con enforceReadOnly; esto es la UX.
            <fieldset
              disabled={readOnly}
              style={{ border: 'none', padding: 0, margin: 0, minInlineSize: 'auto' }}
            >
              <ClientForm
                buttonLabel="Guardar"
                formProps={formProps}
                submitForm={async (values) => {
                  await update(id, values);
                  notification.success({ message: 'Cliente actualizado' });
                  await call(id);
                  return { resetFields: false };
                }}
              />
            </fieldset>
          )}
        </div>
      </Card>

      {minvuEnabled && (
        <>
          {/* Panel de cumplimiento regulatorio: score + breakdown de alertas */}
          <CompliancePanel
            data={complianceData}
            loading={complianceLoading}
            error={complianceError}
          />

          {/* Banner de alertas regulatorias: se muestra solo si hay alertas
              activas (producidas por el cron regulatoryAlertsChecker) */}
          <RegulatoryAlertsBanner idCliente={id} />
        </>
      )}
    </div>
  );
};

export default ClienteInicioPage;
